/**
 * SurveyEFormModal.tsx
 *
 * E-Form dispatch modal opened from the Forms templates list.
 * Pre-selects the form from the clicked row, prompts the user to search
 * and select a patient, then allows displaying / emailing / texting the
 * form link. Plan selection is intentionally omitted.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Mail, MessageSquare } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { formatPhone } from '@/lib/utils';
import type { PatientSummary } from '@/types/patient.types';
import type { Survey } from '@/services/surveys.service';
import { sendSMS, sendEmail } from '@/services/communications.service';
import {
  getEFormReplacementCodes,
  getEFormLink,
  getEmailTemplate,
  getSmsTemplate,
  replaceKeywords,
  type EFormLinkResponse,
} from '@/services/eforms.service';

interface SurveyEFormModalProps {
  survey: Survey | null;
  open: boolean;
  onClose: () => void;
}

interface SectionProps {
  step: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
}

function Section({ step, title, hint, children }: SectionProps) {
  return (
    <section className="space-y-2">
      <header>
        <h3 className="flex items-center text-sm font-semibold text-slate-900">
          <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-white">
            {step}
          </span>
          {title}
        </h3>
        {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      </header>
      {children}
    </section>
  );
}

export function SurveyEFormModal({ survey, open, onClose }: SurveyEFormModalProps) {
  const { isAdmin } = useAuth();
  const practiceName = 'Next Health';

  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [customValues, setCustomValues] = useState<Record<string, string>>({});

  const [linkLoading, setLinkLoading] = useState(false);
  const [linkData, setLinkData] = useState<EFormLinkResponse | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const [sendingSms, setSendingSms] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedDirect, setCopiedDirect] = useState(false);

  const surveyId = survey?.id ?? 0;
  const patientId = patient?.patientId ?? 0;

  useEffect(() => {
    if (!open) {
      setPatient(null);
      setCustomValues({});
      setLinkData(null);
      setLinkError(null);
      setFeedback(null);
      setCopied(false);
      setCopiedDirect(false);
    }
  }, [open]);

  const { data: replacementCodes } = useQuery({
    queryKey: ['eforms-replacement-codes', patientId, surveyId],
    queryFn: () => getEFormReplacementCodes(patientId, surveyId),
    enabled: open && patientId > 0 && surveyId > 0,
    staleTime: 60_000,
  });

  async function refreshLink(nextPatientId: number, nextCustom: Record<string, string>) {
    if (nextPatientId === 0 || surveyId === 0) {
      setLinkData(null);
      setLinkError(null);
      return;
    }
    setLinkLoading(true);
    setLinkError(null);
    try {
      const data = await getEFormLink(nextPatientId, {
        surveyId,
        planId: null,
        customCodes: nextCustom,
      });
      setLinkData(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate link';
      setLinkError(msg);
      setLinkData(null);
    } finally {
      setLinkLoading(false);
    }
  }

  function handlePatientChange(p: PatientSummary | null) {
    setPatient(p);
    setCustomValues({});
    setFeedback(null);
    setLinkData(null);
    setLinkError(null);
    if (p) {
      void refreshLink(p.patientId, {});
    }
  }

  function handleCustomChange(code: string, value: string) {
    const next = { ...customValues, [code]: value };
    setCustomValues(next);
    void refreshLink(patientId, next);
  }

  function handleDisplay() {
    if (!linkData) return;
    window.open(linkData.fullUrl, '_blank', 'noopener,noreferrer');
  }

  function handleCopy() {
    if (!linkData) return;
    void navigator.clipboard.writeText(linkData.shortUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  async function handleSendEmail() {
    if (!linkData || !patient || !patient.email) return;
    if (!window.confirm('E-mail E-Form to this patient?')) return;

    setSendingEmail(true);
    setFeedback(null);
    try {
      let subject: string;
      let body: string;

      const emailTemplateId = survey?.emailContentId;

      if (emailTemplateId && emailTemplateId !== 0) {
        const template = await getEmailTemplate(emailTemplateId);
        subject = template.subject.replace(/\$\{FormUrl\}/g, linkData.shortUrl);
        body = template.body.replace(
          /\$\{FormUrl\}/g,
          `<a href="${linkData.shortUrl}">${linkData.shortUrl}</a>`,
        );
        [subject, body] = await Promise.all([
          replaceKeywords(patientId, subject),
          replaceKeywords(patientId, body),
        ]);
      } else {
        subject = `Electronic Form from ${practiceName}`;
        body = `You have been invited by ${practiceName} to complete a form at the following link:<br><a href="${linkData.shortUrl}">${linkData.shortUrl}</a>`;
      }

      await sendEmail({
        patientId,
        toEmail: patient.email,
        toName: `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || null,
        subject,
        htmlContent: body,
      });
      setFeedback('E-form e-mailed.');
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to send e-mail.');
    } finally {
      setSendingEmail(false);
    }
  }

  async function handleSendSms() {
    if (!linkData || !patient) return;
    const cell = patient.cellPhone;
    if (!cell) return;
    if (!window.confirm('SMS E-Form to this patient?')) return;

    setSendingSms(true);
    setFeedback(null);
    try {
      let message: string;

      const smsTemplateId = survey?.smsId;

      if (smsTemplateId && smsTemplateId !== 0) {
        const template = await getSmsTemplate(smsTemplateId);
        message = template.message.replace(/\$\{FormUrl\}/g, linkData.shortUrl);
        message = await replaceKeywords(patientId, message);
      } else {
        message = `You have been invited by ${practiceName} to complete a form at the following link:\n${linkData.shortUrl}`;
      }

      await sendSMS({
        patientId,
        toPhone: cell,
        message,
      });
      setFeedback('E-form SMS sent.');
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to send SMS.');
    } finally {
      setSendingSms(false);
    }
  }

  const hasLink = !!linkData;
  const hasEmail = !!patient?.email;
  const hasCell = !!patient?.cellPhone;
  const canEmail = hasEmail && hasLink;
  const canSms = hasCell && hasLink;

  const emailDisabledReason = !patient
    ? 'Select a patient first.'
    : !hasLink
      ? 'Generating link...'
      : !hasEmail
        ? 'No email on file.'
        : null;
  const smsDisabledReason = !patient
    ? 'Select a patient first.'
    : !hasLink
      ? 'Generating link...'
      : !hasCell
        ? 'No cell phone on file.'
        : null;

  const isSuccessFeedback = !!feedback && /sent|mailed|copied/i.test(feedback);

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose}>
        Close
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleDisplay}
        disabled={!hasLink || linkLoading}
        title={!hasLink ? 'Select a patient first.' : undefined}
      >
        Display E-Form
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={survey?.title ?? 'E-Form'}
      size="xl"
      footer={footer}
    >
      <div className="space-y-6">
        <Section
          step={1}
          title="Select a patient"
          hint="Search by name, email, phone, or patient ID."
        >
          <PatientSearchInput
            onSelect={handlePatientChange}
            selectedPatient={patient}
            placeholder="Search patients..."
          />
        </Section>

        {patient && replacementCodes && replacementCodes.length > 0 && (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Custom fields for this form</h4>
              <p className="mt-0.5 text-xs text-slate-500">
                Values used to personalize this form before sending.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {replacementCodes.map((code) => (
                <div key={code} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-slate-600">{code}</label>
                  <Input
                    value={customValues[code] ?? ''}
                    onChange={(e) => handleCustomChange(code, e.target.value)}
                    placeholder={`Enter ${code}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <Section
          step={2}
          title="Send to patient"
          hint="Share this form with the patient or open it yourself."
        >
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex gap-4">
              <div className="flex h-[210px] w-[210px] shrink-0 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2">
                {!patient ? (
                  <span className="px-3 text-center text-xs text-slate-500">
                    Select a patient in step 1 to generate a link.
                  </span>
                ) : linkLoading ? (
                  <Spinner />
                ) : linkData ? (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(linkData.shortUrl)}`}
                    alt="E-Form QR code"
                    className="h-[190px] w-[190px]"
                  />
                ) : (
                  <span className="px-3 text-center text-xs text-slate-500">
                    Generating link...
                  </span>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Short link</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      readOnly
                      value={linkData?.shortUrl ?? ''}
                      placeholder="—"
                      className="flex-1 min-w-0 truncate rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-700"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCopy}
                      disabled={!hasLink}
                      className="flex items-center gap-1 px-2"
                      title={!hasLink ? 'Select a patient first.' : 'Copy short link'}
                    >
                      {copied ? (
                        <>
                          <Check className="mr-1 h-3.5 w-3.5" />
                          <span className="text-xs">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="mr-1 h-3.5 w-3.5" />
                          <span className="text-xs">Copy</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex w-full items-center justify-center overflow-hidden text-xs"
                    onClick={handleSendEmail}
                    disabled={!canEmail || sendingEmail}
                    title={emailDisabledReason ?? (hasEmail ? `Email ${patient?.email}` : undefined)}
                  >
                    <Mail className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                      {sendingEmail
                        ? 'Sending...'
                        : hasEmail
                          ? `Email ${patient?.email}`
                          : 'Email'}
                    </span>
                  </Button>
                  {emailDisabledReason && (
                    <p className="mt-1 text-[11px] text-slate-500">{emailDisabledReason}</p>
                  )}
                </div>

                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex w-full items-center justify-center overflow-hidden text-xs"
                    onClick={handleSendSms}
                    disabled={!canSms || sendingSms}
                    title={smsDisabledReason ?? (hasCell ? `SMS ${formatPhone(patient?.cellPhone)}` : undefined)}
                  >
                    <MessageSquare className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                      {sendingSms
                        ? 'Sending...'
                        : hasCell
                          ? `SMS ${formatPhone(patient?.cellPhone)}`
                          : 'SMS'}
                    </span>
                  </Button>
                  {smsDisabledReason && (
                    <p className="mt-1 text-[11px] text-slate-500">{smsDisabledReason}</p>
                  )}
                </div>
              </div>
            </div>

            {linkError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {linkError}
              </div>
            )}
            {feedback && (
              <div
                className={
                  isSuccessFeedback
                    ? 'mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700'
                    : 'mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'
                }
              >
                {feedback}
              </div>
            )}
          </div>
        </Section>

        {isAdmin && survey?.directLink && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h4 className="text-sm font-semibold text-slate-900">Non-Patient Direct Link</h4>
            <p className="mt-1 text-xs text-slate-500">
              Share this link publicly for non-patients. Submissions will create a new lead.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                readOnly
                value={survey.directLink}
                className="flex-1 min-w-0 truncate rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                variant="secondary"
                className="flex items-center gap-1 px-2"
                onClick={() => {
                  void navigator.clipboard.writeText(survey.directLink!).then(() => {
                    setCopiedDirect(true);
                    setTimeout(() => setCopiedDirect(false), 1500);
                  });
                }}
                title="Copy direct link"
              >
                {copiedDirect ? (
                  <>
                    <Check className="mr-1 h-3.5 w-3.5" />
                    <span className="text-xs">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="mr-1 h-3.5 w-3.5" />
                    <span className="text-xs">Copy</span>
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="flex items-center gap-1 px-2"
                onClick={() => window.open(survey.directLink!, '_blank', 'noopener,noreferrer')}
                title="Open in new tab"
              >
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                <span className="text-xs">Open</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
